import type React from "react"
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  ShoppingCart,
  Bus,
  MessageSquare,
  DollarSign,
  Calculator,
  UserCog,
  ClipboardList,
  Briefcase,
  BookOpen,
  HelpCircle,
  FileText,
  PenToolIcon as Tool,
  Package,
  Settings,
  Circle,
  Home,
  Building2,
  CreditCard,
  Wallet,
  Database,
  FolderOpen,
  TrendingUp,
  BarChart2,
  ScrollText,
  MessageCircle,
  Ticket,
  Shield,
  AlertTriangle,
  Landmark,
  UserMinus,
  Layers,
  BadgeCheck,
  Bell,
  Calendar,
  CalendarDays,
} from "lucide-react"
import {
  CanteenIcon,
  LibraryIcon,
  VisitorsIcon,
  AlumniIcon,
  HostelIcon,
  InfirmaryIcon,
  AfterSchoolIcon,
  FacilityIcon,
} from "./sidebar-icons"
import { EventManagementIcon } from "./event-management-icons"

export interface MenuItem {
  title: string
  icon: React.ReactNode
  path?: string
  badge?: string | number
  submenu?: MenuItem[]
  expanded?: boolean
  moduleKey?: string
}

export const getMenuItemsForRole = (role: string): MenuItem[] => {
  switch (role) {
    case "admin":
      return [
        {
          title: "Dashboards",
          icon: <LayoutDashboard size={20} />,
          path: "/",
          badge: "5",
          expanded: false,
          moduleKey: "dashboards",
        },
        {
          title: "Admissions",
          icon: <Users size={20} />,
          path: "/admissions",
          expanded: false,
          moduleKey: "admissions",
          submenu: [
            { title: "Dashboard", icon: <Circle size={8} />, path: "/admissions/dashboard" },
            { title: "Enrollments", icon: <Circle size={8} />, path: "/admissions/enrollments" },
            { title: "Academic Evaluation", icon: <Circle size={8} />, path: "/admissions/academic-evaluation" },
            { title: "Admission Letter", icon: <Circle size={8} />, path: "/admissions/admission-letter" },
            { title: "Map ID", icon: <Circle size={8} />, path: "/admissions/map-id" },
            { title: "Enquiry", icon: <Circle size={8} />, path: "/admissions/enquiry" },
            { title: "Students", icon: <Circle size={8} />, path: "/admissions/students" },
            { title: "Reports", icon: <Circle size={8} />, path: "/admissions/reports" },
            { title: "Settings", icon: <Circle size={8} />, path: "/admissions/settings" },
          ],
        },
        {
          title: "Academics",
          icon: <GraduationCap size={20} />,
          path: "/academics",
          expanded: false,
          moduleKey: "academics",
          submenu: [
            { title: "Academic Configuration", icon: <Circle size={8} />, path: "/academics/configuration" },
            { title: "Academic Year", icon: <Circle size={8} />, path: "/academics/academic-year" },
            { title: "Dashboard", icon: <Circle size={8} />, path: "/academics/dashboard" },
            { title: "Classroom", icon: <Circle size={8} />, path: "/academics/classroom" },
            { title: "Staff Room", icon: <Circle size={8} />, path: "/academics/staff-room" },
            { title: "Report Card", icon: <Circle size={8} />, path: "/academics/report-card" },
            { title: "Content Builder", icon: <Circle size={8} />, path: "/academics/content-builder" },
            { title: "Uploads", icon: <Circle size={8} />, path: "/academics/uploads" },
            { title: "Staff Communication", icon: <Circle size={8} />, path: "/academics/staff-communication" },
            { title: "PTM", icon: <Circle size={8} />, path: "/academics/ptm" },
            { title: "Extra-Curricular Activities", icon: <Circle size={8} />, path: "/academics/extra-curricular" },
            { title: "Events & Calendar", icon: <Circle size={8} />, path: "/academics/events-calendar" },
            { title: "LMS", icon: <Circle size={8} />, path: "/academics/lms" },
            { title: "School Pickup", icon: <Circle size={8} />, path: "/academics/school-pickup" },
            { title: "Reports", icon: <Circle size={8} />, path: "/academics/reports" },
          ],
        },
        // IEP promoted to its own top-level module (2026-09-01) so it
        // can be gated behind a premium subscription tier.
        // ModuleGate keyed on `iep`; the entry is hidden for tenants
        // that don't have the module enabled.
        {
          title: "IEP",
          icon: <BadgeCheck size={20} />,
          path: "/iep",
          expanded: false,
          moduleKey: "iep",
          submenu: [
            { title: "Overview",   icon: <Circle size={8} />, path: "/iep" },
            { title: "Dashboard",  icon: <Circle size={8} />, path: "/iep/dashboard" },
          ],
        },
        {
          title: "eCommerce",
          icon: <ShoppingCart size={20} />,
          path: "/ecommerce",
          expanded: false,
          moduleKey: "ecommerce",
        },
         {
          title: "School Bus",
          icon: <Bus size={20} />,
          path: "/school-bus",
          expanded: false,
          moduleKey: "school-bus",
          submenu: [
            { title: "Dashboard", icon: <Circle size={8} />, path: "/school-bus/dashboard" },
            { title: "Routes and Stops", icon: <Circle size={8} />, path: "/school-bus/routes-stops" },
            { title: "Fees", icon: <Circle size={8} />, path: "/school-bus/fees" },
            { title: "Staff", icon: <Circle size={8} />, path: "/school-bus/staff" },
            { title: "Buses", icon: <Circle size={8} />, path: "/school-bus/buses" },
            { title: "Enrollments", icon: <Circle size={8} />, path: "/school-bus/enrollments" },
            { title: "Bus Tracking", icon: <Circle size={8} />, path: "/school-bus/tracking" },
            { title: "Incidence Type", icon: <Circle size={8} />, path: "/school-bus/incidence-type" },
            { title: "Violation Type", icon: <Circle size={8} />, path: "/school-bus/violation-type" },
            { title: "Authorized Personnel", icon: <Circle size={8} />, path: "/school-bus/authorized-agents" },

            { title: "Transport Reassignment", icon: <Circle size={8} />, path: "/school-bus/transport-reassignment" },
            { title: "Register", icon: <Circle size={8} />, path: "/school-bus/register" },
            { title: "Notifications", icon: <Circle size={8} />, path: "/school-bus/notifications" },
            { title: "Settings", icon: <Circle size={8} />, path: "/school-bus/settings" },
            { title: "Reports", icon: <Circle size={8} />, path: "/school-bus/reports" },
            
          ],
        },
        // 2026-10-21 — Extracurricular lives at /extracurricular/* in
        // tenant-app (App.tsx). The old Next.js /extracurricular-clubs/…
        // paths fell through to the "not migrated" page.
        {
          title: "Extracurricular Clubs",
          icon: <Users size={20} />,
          path: "/extracurricular",
          expanded: false,
          moduleKey: "extracurricular",
          submenu: [
            { title: "Dashboard", icon: <Circle size={8} />, path: "/extracurricular/admin/dashboard" },
            { title: "Clubs", icon: <Circle size={8} />, path: "/extracurricular/admin/clubs" },
            { title: "Enrollments", icon: <Circle size={8} />, path: "/extracurricular/admin/enrollments" },
            { title: "Change Clubs", icon: <Circle size={8} />, path: "/extracurricular/admin/change-club" },
            { title: "Unenrollments", icon: <Circle size={8} />, path: "/extracurricular/admin/unenrollments" },
            { title: "Attendance", icon: <Circle size={8} />, path: "/extracurricular/admin/attendance" },
            { title: "Accounting", icon: <Circle size={8} />, path: "/extracurricular/admin/accounting" },
            { title: "Club Bills", icon: <Circle size={8} />, path: "/extracurricular/admin/club-bills" },
            { title: "Reports", icon: <Circle size={8} />, path: "/extracurricular/admin/reports" },
            { title: "Performance", icon: <Circle size={8} />, path: "/extracurricular/admin/performance" },
            { title: "Settings", icon: <Circle size={8} />, path: "/extracurricular/admin/settings" },
          ],
        },
        {
          title: "Communication",
          icon: <MessageSquare size={20} />,
          path: "/communication",
          expanded: false,
          moduleKey: "communication",
          submenu: [
            { title: "Dashboard", icon: <Circle size={8} />, path: "/communication/dashboard" },
            { title: "General Communication", icon: <Circle size={8} />, path: "/communication/general" },
            { title: "Parent Communication Book", icon: <Circle size={8} />, path: "/communication/parent-book" },
            { title: "Newsletter", icon: <Circle size={8} />, path: "/communication/newsletter" },
            { title: "Campaigns", icon: <Circle size={8} />, path: "/communication/campaigns" },
            { title: "Meeting Mgt", icon: <Circle size={8} />, path: "/communication/meeting-mgt" },
            { title: "Meeting Agenda", icon: <Circle size={8} />, path: "/communication/meeting-agenda" },
            { title: "Meeting Minutes", icon: <Circle size={8} />, path: "/communication/meeting-minutes" },
            { title: "Document", icon: <Circle size={8} />, path: "/communication/document" },
            { title: "Chat", icon: <Circle size={8} />, path: "/communication/chat" },
            { title: "Notes", icon: <Circle size={8} />, path: "/communication/notes" },
            { title: "Notification Templates", icon: <Circle size={8} />, path: "/communication/templates" },
            { title: "Email", icon: <Circle size={8} />, path: "/communication/email" },
            // Poll moved here from Academics → Staff Activities so all
            // school-wide audience-targeted communication tools live in
            // one place.
            { title: "Poll", icon: <Circle size={8} />, path: "/communication/poll" },
            { title: "Settings", icon: <Circle size={8} />, path: "/communication/settings" },
          ],
        },
        {
          title: "Event Management",
          icon: <EventManagementIcon />,
          path: "/event-management",
          expanded: false,
          moduleKey: "event-management",
        },
        {
          title: "Finance",
          icon: <DollarSign size={20} />,
          path: "/finance",
          expanded: false,
          moduleKey: "finance",
          submenu: [
            { title: "Dashboard", icon: <Circle size={8} />, path: "/finance/dashboard" },
            { title: "Collections", icon: <Circle size={8} />, path: "/finance/collections" },
            { title: "Fee Management", icon: <Circle size={8} />, path: "/finance/fee-management" },
            { title: "Parent List", icon: <Circle size={8} />, path: "/finance/parent-list" },
            { title: "Other Customers", icon: <Circle size={8} />, path: "/finance/other-customers" },
            { title: "Quotations", icon: <Circle size={8} />, path: "/finance/quotations" },
            { title: "Sales Order", icon: <Circle size={8} />, path: "/finance/sales-order" },
            { title: "Invoices", icon: <Circle size={8} />, path: "/finance/invoices" },
            { title: "Credits", icon: <Circle size={8} />, path: "/finance/credits" },
            { title: "Discounts", icon: <Circle size={8} />, path: "/finance/discounts" },
            { title: "Fines", icon: <Circle size={8} />, path: "/finance/fines" },
            { title: "Write-off", icon: <Circle size={8} />, path: "/finance/write-off" },
            { title: "Statement", icon: <Circle size={8} />, path: "/finance/statement" },
            { title: "Report", icon: <Circle size={8} />, path: "/finance/report" },
            {
              title: "Referral Program",
              icon: <Circle size={8} />,
              path: "/finance/referral-program",
              expanded: false,

              submenu: [
                { title: "Dashboard", icon: <Circle size={6} />, path: "/finance/referral-program/dashboard" },
                {
                  title: "Referrer List",
                  icon: <Circle size={6} />,
                  path: "/finance/referral-program/referrer-list",
                },
                {
                  title: "Referral Programs",
                  icon: <Circle size={6} />,
                  path: "/finance/referral-program/referral-programs",
                },
                { title: "Referral Rewards", icon: <Circle size={6} />, path: "/finance/referral-program/referral-rewards" },
                { title: "Settings", icon: <Circle size={6} />, path: "/finance/referral-program/settings" },
              ],
            },
            { title: "Settings", icon: <Circle size={8} />, path: "/finance/settings" },
          ],
        },
        {
          title: "Accounting",
          icon: <Calculator size={20} />,
          path: "/accounting",
          expanded: false,
          moduleKey: "accounting",
          // Bug 1.9: chevron-expanded submenu. Ordered by group: Setup →
          // Masters → Transactions → Tax → Banking → Batch → Reports.
          submenu: [
            { title: "Dashboard", icon: <Circle size={8} />, path: "/accounting/dashboard" },
            // Setup
            { title: "Company", icon: <Circle size={8} />, path: "/accounting/company" },
            { title: "Branch", icon: <Circle size={8} />, path: "/accounting/branch" },
            { title: "Currency", icon: <Circle size={8} />, path: "/accounting/currency" },
            { title: "Fiscal Year", icon: <Circle size={8} />, path: "/accounting/fiscal-years" },
            { title: "Accounting Period", icon: <Circle size={8} />, path: "/accounting/accounting-period" },
            // Masters
            { title: "Charts of Accounts", icon: <Circle size={8} />, path: "/accounting/charts-of-accounts" },
            { title: "Cost Center", icon: <Circle size={8} />, path: "/accounting/cost-centers" },
            { title: "Monthly Distribution", icon: <Circle size={8} />, path: "/accounting/monthly-distribution" },
            { title: "Finance Book", icon: <Circle size={8} />, path: "/accounting/finance-book" },
            { title: "Payment Term", icon: <Circle size={8} />, path: "/accounting/payment-term" },
            { title: "Expense Type", icon: <Circle size={8} />, path: "/accounting/expense-type" },
            // Transactions
            { title: "Journal Entry", icon: <Circle size={8} />, path: "/accounting/journal-entry" },
            { title: "Payment Entry", icon: <Circle size={8} />, path: "/accounting/payment-entry" },
            { title: "Expense Entry", icon: <Circle size={8} />, path: "/accounting/expense-entry" },
            { title: "Cash Advances", icon: <Circle size={8} />, path: "/accounting/cash-advances" },
            { title: "Settlement Claims", icon: <Circle size={8} />, path: "/accounting/settlement-claims" },
            { title: "Accruals & Prepayments", icon: <Circle size={8} />, path: "/accounting/accruals-prepayments" },
            { title: "Transaction Allocation", icon: <Circle size={8} />, path: "/accounting/transaction-allocation" },
            // Tax
            { title: "VAT", icon: <Circle size={8} />, path: "/accounting/vat" },
            { title: "WHT", icon: <Circle size={8} />, path: "/accounting/wht" },
            // Banking
            { title: "Banks", icon: <Circle size={8} />, path: "/accounting/banks" },
            { title: "Bank Accounts", icon: <Circle size={8} />, path: "/accounting/bank-accounts" },
            { title: "Bank Reconciliations", icon: <Circle size={8} />, path: "/accounting/bank-reconciliations" },
            { title: "Bank Account Access", icon: <Circle size={8} />, path: "/accounting/bank-account-access" },
            // Batch
            { title: "Account Payable Batch", icon: <Circle size={8} />, path: "/accounting/account-payable-batch" },
            { title: "Account Receivable Batch", icon: <Circle size={8} />, path: "/accounting/account-receivable-batch" },
            { title: "Bank Batch", icon: <Circle size={8} />, path: "/accounting/bank-batch" },
            // Parties
            { title: "Suppliers", icon: <Circle size={8} />, path: "/accounting/suppliers" },
            { title: "Customers", icon: <Circle size={8} />, path: "/accounting/customers" },
            // Budget
            { title: "Budget Control", icon: <Circle size={8} />, path: "/accounting/budget-control" },
            // Reports
            { title: "Reports", icon: <Circle size={8} />, path: "/accounting/reports" },
            { title: "GL Posting Logs", icon: <Circle size={8} />, path: "/accounting/gl-posting-logs" },
            // Settings
            { title: "Settings", icon: <Circle size={8} />, path: "/accounting/settings" },
          ],
        },
         {
          title: "HRMS",
          icon: <UserCog size={20} />,
          path: "/hrms",
          expanded: false,
          moduleKey: "hrms",
          submenu: [
            { title: "Dashboard", icon: <Circle size={8} />, path: "/hrms/dashboard" },
            { title: "HRMS Configurations", icon: <Circle size={8} />, path: "/hrms/configurations" },
            {
              title: "Employee Module",
              icon: <Circle size={8} />,
              path: "/hrms/employee-module",
              expanded: false,
              submenu: [
                { title: "Employee", icon: <Circle size={6} />, path: "/hrms/employee-module/employee" },
                { title: "Employee Group", icon: <Circle size={6} />, path: "/hrms/employee-module/employee-group" },
                { title: "Employee Grade", icon: <Circle size={6} />, path: "/hrms/employee-module/employee-grade" },
                { title: "Work Summary", icon: <Circle size={6} />, path: "/hrms/employee-module/work-summary" },
              ],
            },
            { title: "Department", icon: <Circle size={8} />, path: "/hrms/department" },
            { title: "Designation", icon: <Circle size={8} />, path: "/hrms/designation" },
            { title: "Attendance", icon: <Circle size={8} />, path: "/hrms/attendance" },
            { title: "Recruitment", icon: <Circle size={8} />, path: "/hrms/recruitment" },
            { title: "Personnel Life cycle", icon: <Circle size={8} />, path: "/hrms/personnel-lifecycle" },
            { title: "Loans And Advances", icon: <Circle size={8} />, path: "/hrms/loans-advances" },
            { title: "Leaves", icon: <Circle size={8} />, path: "/hrms/leaves" },
            { title: "Performance Appraisal", icon: <Circle size={8} />, path: "/hrms/performance-appraisal" },
            { title: "Payroll", icon: <Circle size={8} />, path: "/hrms/payroll" },
            { title: "Statutory Remittances", icon: <Circle size={8} />, path: "/hrms/statutory-remittances" },
            { title: "Compensatory Benefits", icon: <Circle size={8} />, path: "/hrms/compensatory-benefits" },
            { title: "Whistle Blowing", icon: <Circle size={8} />, path: "/hrms/whistle-blowing" },
            { title: "Reports", icon: <Circle size={8} />, path: "/hrms/reports" },
          ],
        },
        {
          title: "Procurement",
          icon: <ClipboardList size={20} />,
          path: "/procurement",
          expanded: false,
          moduleKey: "procurement",
        },
        {
          title: "Visitors Management",
          icon: <VisitorsIcon />,
          path: "/visitors-management",
          expanded: false,
          moduleKey: "visitors-management",
        },
        {
          title: "Alumni",
          icon: <AlumniIcon />,
          path: "/alumni",
          expanded: false,
          moduleKey: "alumni",
        },
        {
          title: "Hostel Management",
          icon: <HostelIcon />,
          path: "/hostel-management",
          expanded: false,
          moduleKey: "hostel-management",
        },
        {
          title: "Facility Management",
          icon: <FacilityIcon />,
          path: "/facility-management",
          expanded: false,
          moduleKey: "facility-management",
        },
        {
          title: "After School",
          icon: <AfterSchoolIcon />,
          path: "/after-school",
          expanded: false,
          moduleKey: "after-school",
        },
        {
          title: "Canteen Management",
          icon: <CanteenIcon />,
          path: "/canteen-management",
          expanded: false,
          moduleKey: "canteen-management",
        },
        {
          title: "Library Management",
          icon: <LibraryIcon />,
          path: "/library-management",
          expanded: false,
          moduleKey: "library-management",
        },
        {
          title: "Infirmary",
          icon: <InfirmaryIcon />,
          path: "/infirmary",
          expanded: false,
          moduleKey: "infirmary",
        },
        {
          title: "Fixed Asset Mgt",
          icon: <Briefcase size={20} />,
          path: "/fixed-asset-mgt",
          expanded: false,
          moduleKey: "fixed-asset-mgt",
        },
        {
          title: "LMS",
          icon: <BookOpen size={20} />,
          path: "/lms",
          expanded: false,
          moduleKey: "lms",
          submenu: [
            { title: "Dashboard", icon: <Circle size={8} />, path: "/lms" },
            {
              title: "Course Management",
              icon: <Circle size={8} />,
              path: "/lms/course-management",
              expanded: false,
              submenu: [
                { title: "Courses", icon: <Circle size={6} />, path: "/lms/course-management/courses" },
                { title: "Course Builder", icon: <Circle size={6} />, path: "/lms/course-management/builder" },
                { title: "Categories", icon: <Circle size={6} />, path: "/lms/course-management/categories" },
              ],
            },
            {
              title: "Content Library",
              icon: <Circle size={8} />,
              path: "/lms/content-library",
              expanded: false,
              submenu: [
                { title: "Content Bank", icon: <Circle size={6} />, path: "/lms/content-library/content-bank" },
                { title: "Content Builder", icon: <Circle size={6} />, path: "/lms/content-library/content-builder" },
              ],
            },
            {
              title: "Course Activities",
              icon: <Circle size={8} />,
              path: "/lms/course-activities",
              expanded: false,
              submenu: [
                { title: "Quiz Builder", icon: <Circle size={6} />, path: "/lms/course-activities/builder" },
                { title: "Question Bank", icon: <Circle size={6} />, path: "/lms/course-activities/question-bank" },
                { title: "Assignment", icon: <Circle size={6} />, path: "/lms/course-activities/assignment" },
                { title: "Online Exam", icon: <Circle size={6} />, path: "/lms/course-activities/online-exam" },
                { title: "Project", icon: <Circle size={6} />, path: "/lms/course-activities/project" },
                { title: "Book", icon: <Circle size={6} />, path: "/lms/course-activities/book" },
                { title: "H5P", icon: <Circle size={6} />, path: "/lms/course-activities/h5p" },
                { title: "Lesson", icon: <Circle size={6} />, path: "/lms/course-activities/lesson" },
              ],
            },
            {
              title: "Learning Paths & Programs",
              icon: <Circle size={8} />,
              path: "/lms/learning-paths",
              expanded: false,
              submenu: [
                { title: "Learning Path", icon: <Circle size={6} />, path: "/lms/learning-paths/learning-path" },
              ],
            },
            { title: "Enrollment & Course Assignment", icon: <Circle size={8} />, path: "/lms/enrollment" },
            { title: "Instructors", icon: <Circle size={8} />, path: "/lms/instructors" },
            { title: "Certificates & Compliance", icon: <Circle size={8} />, path: "/lms/certificates" },
            { title: "Progress Tracking & Gradebook", icon: <Circle size={8} />, path: "/lms/progress" },
            { title: "Reporting & Analytics", icon: <Circle size={8} />, path: "/lms/reporting" },
            { title: "Communication", icon: <Circle size={8} />, path: "/lms/communication" },
            { title: "Virtual Classroom", icon: <Circle size={8} />, path: "/lms/virtual-classroom" },
            { title: "Settings", icon: <Circle size={8} />, path: "/lms/settings" },
          ],
        },
        {
          title: "Help Desk",
          icon: <HelpCircle size={20} />,
          path: "/help-desk",
          expanded: false,
          moduleKey: "help-desk",
        },
        {
          title: "Knowledge Base",
          icon: <FileText size={20} />,
          path: "/knowledge-base",
          expanded: false,
          moduleKey: "knowledge-base",
        },
          {
          title: "Terms and Conditions",
          icon: <ScrollText size={20} />,
          path: "/terms-and-conditions",
          expanded: false,
          submenu: [
            { title: "Dashboard",  icon: <Circle size={8} />, path: "/terms-and-conditions/dashboard"  },
            { title: "Templates",  icon: <Circle size={8} />, path: "/terms-and-conditions/templates"  },
            { title: "Categories", icon: <Circle size={8} />, path: "/terms-and-conditions/categories" },
          ],
        },
       
         {
          title: "Tools",
          icon: <Tool size={20} />,
          path: "/tools",
          expanded: false,
          moduleKey: "tools",
          // Only entries that resolve to a real route are listed here.
          // The legacy admin sidebar carried seven extra links (webform,
          // webpage, report-designer, report-automation, print-designer,
          // dashboard-builder, report-analytics) that had no page in
          // EdemicsAllModule and never landed in tenant-app either — they
          // rendered as 404s inside /tools/*. Add them back if/when those
          // pages get built. Module Assignment Manager was ported in FE
          // #31 (with the rest of /tools/*) and is added to the sidebar
          // now that the route exists.
          submenu: [
            { title: "Form Builder",              icon: <Circle size={8} />, path: "/tools/form" },
            { title: "User Management",           icon: <Circle size={8} />, path: "/tools/user-management" },
            { title: "Role Permission Manager",   icon: <Circle size={8} />, path: "/tools/role-permission-manager" },
            { title: "Module Assignment Manager", icon: <Circle size={8} />, path: "/tools/module-assignment-manager" },
            { title: "Workflow",                  icon: <Circle size={8} />, path: "/tools/workflow" },
            { title: "Email Template",            icon: <Circle size={8} />, path: "/tools/email-template" },
          ],
        },
        {
          title: "Wallet Management",
          icon: <Wallet size={20} />,
          path: "/wallet-management",
          expanded: false,
          moduleKey: "wallet-management",
          submenu: [
            { title: "Dashboard", icon: <Circle size={8} />, path: "/wallet-management" },
            { title: "Wallet Transactions", icon: <Circle size={8} />, path: "/wallet-management/transactions" },
            { title: "Wallet Withdrawal",  icon: <Circle size={8} />, path: "/wallet-management/withdrawal" },
            { title: "Wallet Approvers",   icon: <Circle size={8} />, path: "/wallet-management/approvers" },
            { title: "Payment Approvals",  icon: <Circle size={8} />, path: "/wallet-management/payment-approvals" },
            { title: "Wallet Statement",   icon: <Circle size={8} />, path: "/wallet-management/statement" },
            { title: "Wallet Settings",    icon: <Circle size={8} />, path: "/wallet-management/settings" },
          ],
        },
        {
          title: "Inventory Mgt Sys.",
          icon: <Package size={20} />,
          path: "/inventory-mgt-sys",
          expanded: false,
          moduleKey: "inventory-mgt",
        },
        {
          title: "Set up",
          icon: <Settings size={20} />,
          path: "/setup",
          expanded: false,
          moduleKey: "setup",
        },
        {
          title: "PG Settlement",
          icon: <CreditCard size={20} />,
          path: "/pg-settlement",
        },
        {
          title: "Subscription",
          icon: <BadgeCheck size={20} />,
          path: "/subscription",
        },
        {
          title: "Company Setup",
          icon: <Settings size={20} />,
          path: "/company-setup",
          expanded: false,
        },
      ]

    case "teacher":
      return [
        {
          title: "Academics",
          icon: <GraduationCap size={20} />,
          path: "/academics",
          expanded: false,
          moduleKey: "academics",
          submenu: [
            { title: "Academic Year", icon: <Circle size={8} />, path: "/academics/academic-year" },
            { title: "Dashboard", icon: <Circle size={8} />, path: "/academics/dashboard" },
            { title: "Classroom", icon: <Circle size={8} />, path: "/academics/classroom" },
            { title: "Staff Room", icon: <Circle size={8} />, path: "/academics/staff-room" },
            { title: "Report Card", icon: <Circle size={8} />, path: "/academics/report-card" },
            { title: "Content Builder", icon: <Circle size={8} />, path: "/academics/content-builder" },
            { title: "Staff Communication", icon: <Circle size={8} />, path: "/academics/staff-communication" },
            { title: "PTM", icon: <Circle size={8} />, path: "/academics/ptm" },
            { title: "Extra-Curricular Activities", icon: <Circle size={8} />, path: "/academics/extra-curricular" },
            { title: "Events & Calendar", icon: <Circle size={8} />, path: "/academics/events-calendar" },
            { title: "LMS", icon: <Circle size={8} />, path: "/academics/lms" },
            { title: "School Pickup", icon: <Circle size={8} />, path: "/academics/school-pickup" },
          ],
        },
        {
          title: "eCommerce",
          icon: <ShoppingCart size={20} />,
          path: "/ecommerce",
          expanded: false,
          moduleKey: "ecommerce",
        },
        {
          title: "School Bus",
          icon: <Bus size={20} />,
          path: "/school-bus",
          expanded: false,
          moduleKey: "school-bus",
        },
        {
          title: "Communication",
          icon: <MessageSquare size={20} />,
          path: "/communication",
          expanded: false,
          moduleKey: "communication",
        },
        {
          title: "Event Management",
          icon: <EventManagementIcon />,
          path: "/event-management",
          expanded: false,
          moduleKey: "event-management",
        },
        {
          title: "Accounting",
          icon: <Calculator size={20} />,
          path: "/accounting",
          expanded: false,
          moduleKey: "accounting",
          submenu: [
            { title: "Dashboard", icon: <Circle size={8} />, path: "/accounting/dashboard" },
            { title: "Company", icon: <Circle size={8} />, path: "/accounting/company" },
            { title: "Branch", icon: <Circle size={8} />, path: "/accounting/branch" },
            { title: "Currency", icon: <Circle size={8} />, path: "/accounting/currency" },
            { title: "Fiscal Year", icon: <Circle size={8} />, path: "/accounting/fiscal-years" },
            { title: "Accounting Period", icon: <Circle size={8} />, path: "/accounting/accounting-period" },
            { title: "Charts of Accounts", icon: <Circle size={8} />, path: "/accounting/charts-of-accounts" },
            { title: "Cost Center", icon: <Circle size={8} />, path: "/accounting/cost-centers" },
            { title: "Monthly Distribution", icon: <Circle size={8} />, path: "/accounting/monthly-distribution" },
            { title: "Finance Book", icon: <Circle size={8} />, path: "/accounting/finance-book" },
            { title: "Payment Term", icon: <Circle size={8} />, path: "/accounting/payment-term" },
            { title: "Expense Type", icon: <Circle size={8} />, path: "/accounting/expense-type" },
            { title: "Journal Entry", icon: <Circle size={8} />, path: "/accounting/journal-entry" },
            { title: "Payment Entry", icon: <Circle size={8} />, path: "/accounting/payment-entry" },
            { title: "Expense Entry", icon: <Circle size={8} />, path: "/accounting/expense-entry" },
            { title: "Cash Advances", icon: <Circle size={8} />, path: "/accounting/cash-advances" },
            { title: "Settlement Claims", icon: <Circle size={8} />, path: "/accounting/settlement-claims" },
            { title: "Accruals & Prepayments", icon: <Circle size={8} />, path: "/accounting/accruals-prepayments" },
            { title: "Transaction Allocation", icon: <Circle size={8} />, path: "/accounting/transaction-allocation" },
            { title: "VAT", icon: <Circle size={8} />, path: "/accounting/vat" },
            { title: "WHT", icon: <Circle size={8} />, path: "/accounting/wht" },
            { title: "Banks", icon: <Circle size={8} />, path: "/accounting/banks" },
            { title: "Bank Accounts", icon: <Circle size={8} />, path: "/accounting/bank-accounts" },
            { title: "Bank Reconciliations", icon: <Circle size={8} />, path: "/accounting/bank-reconciliations" },
            { title: "Account Payable Batch", icon: <Circle size={8} />, path: "/accounting/account-payable-batch" },
            { title: "Account Receivable Batch", icon: <Circle size={8} />, path: "/accounting/account-receivable-batch" },
            { title: "Suppliers", icon: <Circle size={8} />, path: "/accounting/suppliers" },
            { title: "Customers", icon: <Circle size={8} />, path: "/accounting/customers" },
            { title: "Budget Control", icon: <Circle size={8} />, path: "/accounting/budget-control" },
            { title: "Reports", icon: <Circle size={8} />, path: "/accounting/reports" },
            { title: "GL Posting Logs", icon: <Circle size={8} />, path: "/accounting/gl-posting-logs" },
            { title: "Settings", icon: <Circle size={8} />, path: "/accounting/settings" },
          ],
        },
        {
          title: "Visitors Management",
          icon: <VisitorsIcon />,
          path: "/visitors-management",
          expanded: false,
          moduleKey: "visitors-management",
        },
        {
          title: "Canteen Management",
          icon: <CanteenIcon />,
          path: "/canteen-management",
          expanded: false,
          moduleKey: "canteen-management",
        },
        {
          title: "Library Management",
          icon: <LibraryIcon />,
          path: "/library-management",
          expanded: false,
          moduleKey: "library-management",
        },
        {
          title: "HRMS",
          icon: <UserCog size={20} />,
          path: "/hrms",
          expanded: false,
          moduleKey: "hrms",
        },
        {
          title: "Infirmary",
          icon: <InfirmaryIcon />,
          path: "/infirmary",
          expanded: false,
          moduleKey: "infirmary",
        },
        {
          title: "LMS",
          icon: <BookOpen size={20} />,
          path: "/lms",
          expanded: false,
          moduleKey: "lms",
          submenu: [
            { title: "Dashboard", icon: <Circle size={8} />, path: "/lms" },
            {
              title: "Course Management",
              icon: <Circle size={8} />,
              path: "/lms/course-management",
              expanded: false,
              submenu: [
                { title: "Courses", icon: <Circle size={6} />, path: "/lms/course-management/courses" },
                { title: "Course Builder", icon: <Circle size={6} />, path: "/lms/course-management/builder" },
                { title: "Categories", icon: <Circle size={6} />, path: "/lms/course-management/categories" },
              ],
            },
            {
              title: "Content Library",
              icon: <Circle size={8} />,
              path: "/lms/content-library",
              expanded: false,
              submenu: [
                { title: "Content Bank", icon: <Circle size={6} />, path: "/lms/content-library/content-bank" },
                { title: "Content Builder", icon: <Circle size={6} />, path: "/lms/content-library/content-builder" },
              ],
            },
            {
              title: "Course Activities",
              icon: <Circle size={8} />,
              path: "/lms/course-activities",
              expanded: false,
              submenu: [
                { title: "Quiz Builder", icon: <Circle size={6} />, path: "/lms/course-activities/builder" },
                { title: "Question Bank", icon: <Circle size={6} />, path: "/lms/course-activities/question-bank" },
                { title: "Assignment", icon: <Circle size={6} />, path: "/lms/course-activities/assignment" },
                { title: "Online Exam", icon: <Circle size={6} />, path: "/lms/course-activities/online-exam" },
                { title: "Project", icon: <Circle size={6} />, path: "/lms/course-activities/project" },
                { title: "Book", icon: <Circle size={6} />, path: "/lms/course-activities/book" },
                { title: "H5P", icon: <Circle size={6} />, path: "/lms/course-activities/h5p" },
                { title: "Lesson", icon: <Circle size={6} />, path: "/lms/course-activities/lesson" },
              ],
            },
            {
              title: "Learning Paths & Programs",
              icon: <Circle size={8} />,
              path: "/lms/learning-paths",
              expanded: false,
              submenu: [
                { title: "Learning Path", icon: <Circle size={6} />, path: "/lms/learning-paths/learning-path" },
              ],
            },
            { title: "Enrollment & Course Assignment", icon: <Circle size={8} />, path: "/lms/enrollment" },
            { title: "Instructors", icon: <Circle size={8} />, path: "/lms/instructors" },
            { title: "Certificates & Compliance", icon: <Circle size={8} />, path: "/lms/certificates" },
            { title: "Progress Tracking & Gradebook", icon: <Circle size={8} />, path: "/lms/progress" },
            { title: "Reporting & Analytics", icon: <Circle size={8} />, path: "/lms/reporting" },
            { title: "Communication", icon: <Circle size={8} />, path: "/lms/communication" },
            { title: "Virtual Classroom", icon: <Circle size={8} />, path: "/lms/virtual-classroom" },
            { title: "Settings", icon: <Circle size={8} />, path: "/lms/settings" },
          ],
        },
      ]

    case "parent":
      return [
       {
          title: "My Dashboard",
          icon: <LayoutDashboard size={20} />,
          path: "/parent-portal",
          expanded: false,
        },
        {
          title: "Admissions",
          icon: <ClipboardList size={20} />,
          path: "/admissions/parent-portal",
          expanded: false,
          submenu: [
            { title: "Parent Enrollments", icon: <Circle size={8} />, path: "/admissions/parent-enrollments" },
            { title: "Student Information System", icon: <Circle size={8} />, path: "/admissions/parent-students" },
            { title: "My Referrals", icon: <Circle size={8} />, path: "/admissions/parent-referrals" },
          ],
        },
        {
          title: "Parent Meetings",
          icon: <Users size={20} />,
          path: "/parent-meetings/parent-portal",
          expanded: false,
          submenu: [
            { title: "My Meetings", icon: <Circle size={8} />, path: "/parent-meetings/my-meetings" },
            { title: "My PM Reports", icon: <Circle size={8} />, path: "/parent-meetings/my-pm-reports" },
          ],
        },
        {
          title: "Finance",
          icon: <DollarSign size={20} />,
          path: "/finance/parent-portal",
          expanded: false,
          submenu: [
            { title: "School Fee", icon: <Circle size={8} />, path: "/finance/parent-school-fee" },
            { title: "My Wallet", icon: <Circle size={8} />, path: "/finance/parent-my-wallet" },
            { title: "Statement", icon: <Circle size={8} />, path: "/finance/parent-statement" },
            { title: "Receipts", icon: <Circle size={8} />, path: "/finance/parent-receipts" },
            { title: "Scholarship", icon: <Circle size={8} />, path: "/finance/parent-scholarship" },
          ],
        },
        {
          title: "School Pickup",
          icon: <Users size={20} />,
          path: "/school-pickup/parent-portal",
          expanded: false,
          submenu: [
            { title: "Pickup Personnel", icon: <Circle size={8} />, path: "/school-pickup/parent-pickup-personnel" },
            { title: "Pickup", icon: <Circle size={8} />, path: "/school-pickup/parent-pickup" },
            { title: "Delegate Pickup", icon: <Circle size={8} />, path: "/school-pickup/parent-delegate-pickup" },
          ],
        },
        {
          title: "Academics",
          icon: <GraduationCap size={20} />,
          path: "/academics",
          expanded: false,
          moduleKey: "academics",
          submenu: [
            { title: "Report Card", icon: <Circle size={8} />, path: "/academics/report-card" },
            { title: "Extra-Curricular Activities", icon: <Circle size={8} />, path: "/academics/extra-curricular" },
            { title: "Events & Calendar", icon: <Circle size={8} />, path: "/academics/events-calendar" },
            { title: "School Pickup", icon: <Circle size={8} />, path: "/academics/school-pickup" },
          ],
        },
        {
          title: "PTM",
          icon: <Users size={20} />,
          path: "/ptm",
          expanded: false,
          moduleKey: "academics",
        },
      
          {
          title: "School Bus",
          icon: <Bus size={20} />,
          path: "/school-bus/parent-portal",
          expanded: false,
          moduleKey: "school-bus",
          submenu: [
            { title: "My Children", icon: <Circle size={8} />, path: "/school-bus/my-children" },
            { title: "Bus Tracking", icon: <Circle size={8} />, path: "/school-bus/parent-tracking" },
            { title: "Enrollment", icon: <Circle size={8} />, path: "/school-bus/parent-enrollment" },
            { title: "Payments", icon: <Circle size={8} />, path: "/school-bus/payments" },
            { title: "Authorized Personnel", icon: <Circle size={8} />, path: "/school-bus/parent-authorized-agent" },
            { title: "Register", icon: <Circle size={8} />, path: "/school-bus/register" },
            { title: "Routes and Bus Stops", icon: <Circle size={8} />, path: "/school-bus/parent-routes-stops" },
          ],
        },
        // 2026-10-21 — parents get the parent pages of the module
        // (/extracurricular/parent/*). The old entries opened the admin
        // pages under the Next.js /extracurricular-clubs/… paths.
        {
          title: "Extracurricular Clubs",
          icon: <Users size={20} />,
          path: "/extracurricular/parent/dashboard",
          expanded: false,
          moduleKey: "extracurricular",
          submenu: [
            { title: "Dashboard", icon: <Circle size={8} />, path: "/extracurricular/parent/dashboard" },
            { title: "Enrollments", icon: <Circle size={8} />, path: "/extracurricular/parent/enrollments" },
            { title: "Club Change", icon: <Circle size={8} />, path: "/extracurricular/parent/club-change" },
            { title: "Performance", icon: <Circle size={8} />, path: "/extracurricular/parent/performance" },
          ],
        },
        {
          title: "Communication",
          icon: <MessageSquare size={20} />,
          path: "/communication",
          expanded: false,
          moduleKey: "communication",
          submenu: [
            { title: "Dashboard", icon: <Circle size={8} />, path: "/communication/dashboard" },
            { title: "General Communication", icon: <Circle size={8} />, path: "/communication/general" },
            { title: "Parent Communication Book", icon: <Circle size={8} />, path: "/communication/parent-book" },
            { title: "Newsletter", icon: <Circle size={8} />, path: "/communication/newsletter" },
            { title: "Campaigns", icon: <Circle size={8} />, path: "/communication/campaigns" },
            { title: "Meeting Mgt", icon: <Circle size={8} />, path: "/communication/meeting-mgt" },
            { title: "Meeting Agenda", icon: <Circle size={8} />, path: "/communication/meeting-agenda" },
            { title: "Meeting Minutes", icon: <Circle size={8} />, path: "/communication/meeting-minutes" },
            { title: "Document", icon: <Circle size={8} />, path: "/communication/document" },
            { title: "Chat", icon: <Circle size={8} />, path: "/communication/chat" },
            { title: "Notes", icon: <Circle size={8} />, path: "/communication/notes" },
            { title: "Notification Templates", icon: <Circle size={8} />, path: "/communication/templates" },
            { title: "Email", icon: <Circle size={8} />, path: "/communication/email" },
            // Poll moved here from Academics → Staff Activities so all
            // school-wide audience-targeted communication tools live in
            // one place.
            { title: "Poll", icon: <Circle size={8} />, path: "/communication/poll" },
            { title: "Settings", icon: <Circle size={8} />, path: "/communication/settings" },
          ],
        },
        {
          title: "Event Management",
          icon: <EventManagementIcon />,
          path: "/event-management",
          expanded: false,
          moduleKey: "event-management",
        },
        {
          title: "Visitors Management",
          icon: <VisitorsIcon />,
          path: "/visitors-management",
          expanded: false,
          moduleKey: "visitors-management",
        },
        {
          title: "Infirmary",
          icon: <InfirmaryIcon />,
          path: "/infirmary",
          expanded: false,
          moduleKey: "infirmary",
        },
        {
          title: "Library Management",
          icon: <LibraryIcon />,
          path: "/library-management",
          expanded: false,
          moduleKey: "library-management",
        },
      ]

    case "student":
      // Locked to Ayodeji's demo brief (2026-09-01): Dashboard,
      // Homework, Quiz, Events & Calendar, Communication, Chat,
      // Notifications, plus the read-only Academics rail (Classroom,
      // Report Card, LMS, Extra-Curricular). Meeting and
      // Request-Appointment are explicitly NOT in this menu — students
      // are barred from those surfaces. Do not add them back without
      // a stakeholder sign-off.
      return [
        {
          title: "Dashboard",
          icon: <LayoutDashboard size={20} />,
          path: "/student/dashboard",
          expanded: false,
        },
        {
          title: "Homework",
          icon: <BookOpen size={20} />,
          path: "/student/homework",
          expanded: false,
        },
        {
          title: "Quiz",
          icon: <HelpCircle size={20} />,
          path: "/student/quiz",
          expanded: false,
        },
        {
          title: "Events & Calendar",
          icon: <CalendarDays size={20} />,
          path: "/student/events-calendar",
          expanded: false,
        },
        {
          title: "Communication",
          icon: <MessageSquare size={20} />,
          path: "/student/communication",
          expanded: false,
        },
        {
          title: "Chat",
          icon: <MessageCircle size={20} />,
          path: "/student/chat",
          expanded: false,
        },
        {
          title: "Notifications",
          icon: <Bell size={20} />,
          path: "/student/notifications",
          expanded: false,
        },
        {
          title: "Academics",
          icon: <GraduationCap size={20} />,
          path: "/academics",
          expanded: false,
          moduleKey: "academics",
          submenu: [
            { title: "Classroom", icon: <Circle size={8} />, path: "/academics/classroom" },
            { title: "Report Card", icon: <Circle size={8} />, path: "/academics/report-card" },
            { title: "Extra-Curricular Activities", icon: <Circle size={8} />, path: "/academics/extra-curricular" },
            { title: "LMS", icon: <Circle size={8} />, path: "/academics/lms" },
          ],
        },
        {
          title: "School Bus",
          icon: <Bus size={20} />,
          path: "/school-bus",
          expanded: false,
          moduleKey: "school-bus",
        },
        {
          title: "Library Management",
          icon: <LibraryIcon />,
          path: "/library-management",
          expanded: false,
          moduleKey: "library-management",
        },
        {
          title: "Infirmary",
          icon: <InfirmaryIcon />,
          path: "/infirmary",
          expanded: false,
          moduleKey: "infirmary",
        },
        {
          title: "After School",
          icon: <AfterSchoolIcon />,
          path: "/after-school",
          expanded: false,
          moduleKey: "after-school",
        },
      ]

    case "alumni":
      return [
        {
          title: "Alumni Network",
          icon: <AlumniIcon />,
          path: "/alumni",
          expanded: false,
          moduleKey: "alumni",
        },
        {
          title: "Events & Calendar",
          icon: <Calendar size={20} />,
          path: "/events-calendar",
          expanded: false,
          moduleKey: "event-management",
        },
        {
          title: "Communication",
          icon: <MessageSquare size={20} />,
          path: "/communication",
          expanded: false,
          moduleKey: "communication",
        },
        {
          title: "Library Management",
          icon: <LibraryIcon />,
          path: "/library-management",
          expanded: false,
          moduleKey: "library-management",
        },
      ]
     case "super-admin":
      return [
        {
          title: "Dashboard",
          icon: <LayoutDashboard size={20} />,
          path: "/super-admin",
          expanded: false,
        },
        {
          title: "Tenant Management",
          icon: <Building2 size={20} />,
          path: "/super-admin/tenant-management",
          expanded: false,
        },
        {
          title: "Module Setup",
          icon: <Layers size={20} />,
          path: "/super-admin/module-setup",
          expanded: false,
        },
        {
          title: "Module Visibility",
          icon: <Layers size={20} />,
          path: "/super-admin/module-visibility",
          expanded: false,
        },
        {
          title: "Subscriptions",
          icon: <CreditCard size={20} />,
          path: "/super-admin/subscriptions",
          expanded: false,
          submenu: [
            { title: "Plans",                icon: <Circle size={8} />, path: "/super-admin/subscriptions" },
            // Phase D1 (task #127) — cross-tenant SubscriptionCycle admin.
            { title: "Cycles",               icon: <Circle size={8} />, path: "/super-admin/subscription-cycles" },
          ],
        },
        {
          title: "Payments",
          icon: <DollarSign size={20} />,
          path: "/super-admin/payments",
          expanded: false,
          submenu: [
            { title: "Records",              icon: <Circle size={8} />, path: "/super-admin/payments" },
            // Phase D1 (task #127) — Phase A PaymentAttempt + PaymentWebhookEvent surface.
            { title: "History",              icon: <Circle size={8} />, path: "/super-admin/payments-history" },
            { title: "Webhook Reconciliation", icon: <Circle size={8} />, path: "/super-admin/webhook-reconciliation" },
          ],
        },
        {
          title: "Wallet Management",
          icon: <Wallet size={20} />,
          path: "/super-admin/wallet-management",
          expanded: false,
          submenu: [
            { title: "General Settings",    icon: <Circle size={8} />, path: "/super-admin/wallet-management/general-settings" },
            { title: "Tenant Provisioning", icon: <Circle size={8} />, path: "/super-admin/wallet-management/tenant-provisioning" },
            { title: "Tenants Wallet",      icon: <Circle size={8} />, path: "/super-admin/wallet-management/tenants-wallet" },
            { title: "Wallet Funding",      icon: <Circle size={8} />, path: "/super-admin/wallet-management/wallet-funding" },
            { title: "Payment Approvers",   icon: <Circle size={8} />, path: "/super-admin/wallet-management/payment-approvers" },
            { title: "Withdrawal Request",  icon: <Circle size={8} />, path: "/super-admin/wallet-management/withdrawal-request" },
          ],
        },
        {
          title: "Database Management",
          icon: <Database size={20} />,
          path: "/super-admin/database-management",
          expanded: false,
        },
        {
          title: "Folder Management",
          icon: <FolderOpen size={20} />,
          path: "/super-admin/folder-management",
          expanded: false,
        },
        {
          title: "Revenue & Finance",
          icon: <TrendingUp size={20} />,
          path: "/super-admin/revenue-finance",
          expanded: false,
        },
        {
          title: "Usage & Licensing",
          icon: <BarChart2 size={20} />,
          path: "/super-admin/usage-licensing",
          expanded: false,
        },
        {
          title: "Audit Logs",
          icon: <ScrollText size={20} />,
          path: "/super-admin/audit-logs",
          expanded: false,
        },
        {
          title: "Communications",
          icon: <MessageCircle size={20} />,
          path: "/super-admin/communications",
          expanded: false,
        },
        {
          title: "Support & Tickets",
          icon: <Ticket size={20} />,
          path: "/super-admin/support-tickets",
          expanded: false,
          submenu: [
            { title: "Support Group",  icon: <Circle size={8} />, path: "/super-admin/support-tickets/support-group" },
            { title: "Tickets",        icon: <Circle size={8} />, path: "/super-admin/support-tickets/tickets" },
            { title: "Ticket Type",    icon: <Circle size={8} />, path: "/super-admin/support-tickets/ticket-type" },
            { title: "Settings",       icon: <Circle size={8} />, path: "/super-admin/support-tickets/settings" },
          ],
        },
        {
          title: "Compliance & Legal",
          icon: <Shield size={20} />,
          path: "/super-admin/compliance-legal",
          expanded: false,
        },
        {
          title: "Whistle Blower Mgt",
          icon: <AlertTriangle size={20} />,
          path: "/super-admin/whistle-blower",
          expanded: false,
        },
        {
          title: "Payment Gateways",
          icon: <Landmark size={20} />,
          path: "/super-admin/payment-gateway",
          expanded: false,
          submenu: [
            // Platform-level: which providers Edemics supports, their API
            // keys + enable/disable toggles. One row per provider,
            // shared across every tenant. See
            // /super-admin/payment-gateway/settings.
            { title: "Platform Configuration", icon: <Circle size={8} />, path: "/super-admin/payment-gateway/settings" },
            // Per-tenant setup: settlement accounts, fee policy overrides,
            // remittance preferences. One row per tenant.
            { title: "Tenant Setups",          icon: <Circle size={8} />, path: "/super-admin/payment-gateway" },
          ],
        },
        {
          title: "Churn & Retention",
          icon: <UserMinus size={20} />,
          path: "/super-admin/churn-retention",
          expanded: false,
        },
      ]

    default:
      return []
  }
}